import {ReactNode, TouchEvent, useRef} from "react";

interface IProps {
    onClose: () => void;
    threshold?: number;
    className?: string;
    children: ReactNode;
}

export default function SwipeToClose({onClose, threshold = 90, className, children}: IProps) {
    const ref = useRef<HTMLDivElement>(null);
    const startY = useRef(0);
    const delta = useRef(0);
    const dragging = useRef(false);
    const allow = useRef(true);

    const isMobile = () =>
        typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;

    const nearestScrollable = (node: HTMLElement | null): HTMLElement | null => {
        let el = node;
        while (el && el !== ref.current) {
            if (el.scrollHeight > el.clientHeight) {
                const oy = getComputedStyle(el).overflowY;
                if (oy === "auto" || oy === "scroll") return el;
            }
            el = el.parentElement;
        }
        return null;
    };

    const onTouchStart = (e: TouchEvent<HTMLDivElement>) => {
        if (!isMobile()) return;
        const scrollable = nearestScrollable(e.target as HTMLElement);
        allow.current = !scrollable || scrollable.scrollTop <= 0;
        startY.current = e.touches[0].clientY;
        delta.current = 0;
        dragging.current = true;
        if (ref.current) ref.current.style.transition = "none";
    };

    const onTouchMove = (e: TouchEvent<HTMLDivElement>) => {
        if (!dragging.current || !allow.current || !ref.current) return;
        const dy = e.touches[0].clientY - startY.current;
        if (dy <= 0) {
            delta.current = 0;
            ref.current.style.transform = "";
            return;
        }
        delta.current = dy;
        ref.current.style.transform = `translateY(${dy}px)`;
    };

    const onTouchEnd = () => {
        if (!dragging.current || !ref.current) return;
        dragging.current = false;
        ref.current.style.transition = "transform 0.25s ease";
        if (delta.current > threshold) {
            ref.current.style.transform = "translateY(120vh)";
            window.setTimeout(onClose, 220);
        } else {
            ref.current.style.transform = "";
        }
        delta.current = 0;
    };

    return (
        <div
            ref={ref}
            className={className}
            style={{touchAction: "pan-y"}}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onTouchCancel={onTouchEnd}
        >
            {children}
        </div>
    );
}
